import React, { useEffect, useState } from 'react';

import { db } from '../firebase/firebase';

interface Reaction {
    id: string;
    emoji: string;
    userId: string;
    timestamp: number;
    x: number;
    y: number;
}

interface EmojiReactionsProps {
    roomCode: string;
    userId: string;
}

const EMOJIS = ['🎉', '👏', '❤️', '😂', '🚀', '🔥', '🙌', '🤔', '😱', '🪱'];

const EmojiReactions: React.FC<EmojiReactionsProps> = ({
    roomCode,
    userId,
}) => {
    const [reactions, setReactions] = useState<Reaction[]>([]);
    const [showPicker, setShowPicker] = useState(false);
    const [buttonDisabled, setButtonDisabled] = useState(false);

    useEffect(() => {
        const reactionsRef = db.ref(`rooms/${roomCode}/reactions`);

        const handleNewReaction = (snapshot: any) => {
            const reaction = snapshot.val();
            if (reaction) {
                const newReaction = { ...reaction, id: snapshot.key };
                setReactions((prev) => [...prev, newReaction]);

                setTimeout(() => {
                    setReactions((prev) =>
                        prev.filter((r) => r.id !== snapshot.key),
                    );
                }, 4000);

                setTimeout(() => {
                    reactionsRef.child(snapshot.key).remove();
                }, 4500);
            }
        };

        reactionsRef.on('child_added', handleNewReaction);

        return () => {
            reactionsRef.off('child_added', handleNewReaction);
        };
    }, [roomCode]);

    const sendReaction = (emoji: string) => {
        const x = 10 + Math.random() * 80;
        const y = 10 + Math.random() * 80;

        const reaction = {
            emoji,
            userId,
            timestamp: Date.now(),
            x,
            y,
        };

        db.ref(`rooms/${roomCode}/reactions`).push(reaction);
        setShowPicker(false);

        setButtonDisabled(true);
        setTimeout(() => {
            setButtonDisabled(false);
        }, 10000);
    };

    return (
        <>
            <div
                className="emoji-reactions-container"
                style={{
                    position: 'fixed',
                    bottom: '1rem',
                    right: '1rem',
                    zIndex: 9999,
                }}
            >
                <div style={{ position: 'relative' }}>
                    {showPicker && (
                        <div
                            className="emoji-picker-panel"
                            style={{
                                position: 'absolute',
                                bottom: '4rem',
                                right: '0',
                                backgroundColor: 'white',
                                borderRadius: '0.5rem',
                                boxShadow:
                                    '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                                padding: '1rem',
                                display: 'grid',
                                gridTemplateColumns: 'repeat(5, 1fr)',
                                gap: '0.75rem',
                                border: '1px solid #e5e7eb',
                                minWidth: '280px',
                            }}
                        >
                            {EMOJIS.map((emoji) => (
                                <button
                                    key={emoji}
                                    onClick={() => sendReaction(emoji)}
                                    style={{
                                        fontSize: '1.875rem',
                                        padding: '0.5rem',
                                        borderRadius: '0.5rem',
                                        border: 'none',
                                        backgroundColor: 'transparent',
                                        cursor: 'pointer',
                                        width: '48px',
                                        height: '48px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        transition:
                                            'transform 0.2s, background-color 0.2s',
                                    }}
                                    onMouseEnter={(e) => {
                                        e.currentTarget.style.transform =
                                            'scale(1.25)';
                                        e.currentTarget.style.backgroundColor =
                                            '#f3f4f6';
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.transform =
                                            'scale(1)';
                                        e.currentTarget.style.backgroundColor =
                                            'transparent';
                                    }}
                                >
                                    {emoji}
                                </button>
                            ))}
                        </div>
                    )}

                    <button
                        disabled={buttonDisabled}
                        onClick={() => setShowPicker(!showPicker)}
                        style={{
                            backgroundColor: buttonDisabled
                                ? '#9CA3AF'
                                : '#3182ce',
                            color: 'white',
                            borderRadius: '9999px',
                            padding: '1rem',
                            border: 'none',
                            cursor: buttonDisabled ? 'not-allowed' : 'pointer',
                            boxShadow: buttonDisabled
                                ? 'none'
                                : '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
                            transition: 'all 0.2s',
                            transform: buttonDisabled ? 'scale(1)' : 'scale(1)',
                            opacity: buttonDisabled ? 0.5 : 1,
                        }}
                        onMouseEnter={(e) => {
                            if (!buttonDisabled) {
                                e.currentTarget.style.backgroundColor =
                                    '#2c5282';
                                e.currentTarget.style.transform = 'scale(1.1)';
                            }
                        }}
                        onMouseLeave={(e) => {
                            if (!buttonDisabled) {
                                e.currentTarget.style.backgroundColor =
                                    '#3182ce';
                                e.currentTarget.style.transform = 'scale(1)';
                            }
                        }}
                    >
                        {!buttonDisabled ? (
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                style={{ height: '1.5rem', width: '1.5rem' }}
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                />
                            </svg>
                        ) : (
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                fill="none"
                                viewBox="0 0 24 24"
                                strokeWidth={1.5}
                                stroke="currentColor"
                                style={{ height: '1.5rem', width: '1.5rem' }}
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    d="M18.364 18.364A9 9 0 0 0 5.636 5.636m12.728 12.728A9 9 0 0 1 5.636 5.636m12.728 12.728L5.636 5.636"
                                />
                            </svg>
                        )}
                    </button>
                </div>
            </div>

            {reactions.map((reaction) => (
                <div
                    key={reaction.id}
                    className="emoji-fountain"
                    style={{
                        left: `${reaction.x}%`,
                        top: `${reaction.y}%`,
                    }}
                >
                    <div className="emoji-core">{reaction.emoji}</div>

                    <div className="rainbow-ring ring-1"></div>
                    <div className="rainbow-ring ring-2"></div>
                    <div className="rainbow-ring ring-3"></div>

                    <div className="emoji-burst burst-1">{reaction.emoji}</div>
                    <div className="emoji-burst burst-2">{reaction.emoji}</div>
                    <div className="emoji-burst burst-3">{reaction.emoji}</div>
                    <div className="emoji-burst burst-4">{reaction.emoji}</div>
                    <div className="emoji-burst burst-5">{reaction.emoji}</div>
                    <div className="emoji-burst burst-6">{reaction.emoji}</div>
                    <div className="emoji-burst burst-7">{reaction.emoji}</div>
                    <div className="emoji-burst burst-8">{reaction.emoji}</div>

                    <div className="sparkle-container">
                        <div className="sparkle s1">✨</div>
                        <div className="sparkle s2">⭐</div>
                        <div className="sparkle s3">💫</div>
                        <div className="sparkle s4">✨</div>
                        <div className="sparkle s5">⭐</div>
                        <div className="sparkle s6">💫</div>
                    </div>
                </div>
            ))}
        </>
    );
};

export default EmojiReactions;
